import React from 'react';
import { ExternalLink, Flag } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { formatAmount, formatDateTimeUTC } from './format';
import { formatShortUTC } from './timeline';
import { KIND_COLORS, periodTotals, railView, startReason, storyRows } from './storyRail';

// "Now" uses the ink colour (tailwind ink.DEFAULT); style props need the value itself.
const NOW_COLOR = '#f4f1ec';
// Stripes for the part of the week before the period starts (it does not count).
const BEFORE_START = { backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 5px, transparent 5px 10px)' };
const DASHED_LINE = { backgroundImage: 'repeating-linear-gradient(rgba(255,255,255,0.18) 0 4px, transparent 4px 8px)' };

const colorOf = (row) => (row.type === 'now' ? NOW_COLOR : KIND_COLORS[row.kind] || NOW_COLOR);

// Rail: the week as a bar — striped before the start point, the elapsed part of the period
// filled, a dot per transaction, the start marker and the now line.
function Rail({ rail }) {
  return (
    <div className="space-y-2">
      <div className="relative h-3.5 rounded-full bg-surface-2" style={BEFORE_START}>
        {/* The period: an opaque base so the stripes stay before the start only. */}
        <div className="absolute inset-y-0 right-0 overflow-hidden rounded-r-full bg-surface-2" style={{ left: `${rail.startPct}%` }}>
          <div className="h-full w-full bg-brand-blue/20" />
        </div>
        <div className="absolute inset-y-0 rounded-l-full bg-brand-blue" style={{ left: `${rail.startPct}%`, width: `${rail.elapsedPct}%` }} />
        {rail.dots.map((d) => (
          <span
            key={d.key}
            className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface-1"
            style={{ left: `${d.pct}%`, background: KIND_COLORS[d.kind] }}
          />
        ))}
        <span className="absolute -inset-y-1 w-[3px] rounded-sm" style={{ left: `${rail.startPct}%`, background: KIND_COLORS[rail.startKind] }} />
        {rail.nowPct !== null && <span className="absolute -inset-y-1.5 w-0.5 bg-ink" style={{ left: `${rail.nowPct}%` }} title="Now" />}
      </div>
      <div className="flex justify-between gap-2 font-mono text-xs font-medium text-ink-mute">
        <span>{formatShortUTC(rail.weekStart)}</span>
        {rail.elapsed && <span className="text-center">{rail.elapsed} elapsed · {rail.left} left</span>}
        <span>{formatShortUTC(rail.weekEnd)}</span>
      </div>
    </div>
  );
}

// Marker: the dot on the story line — a flag for the start, a filled dot for a transaction, a
// ring for now and the period's end.
function Marker({ row }) {
  const color = colorOf(row);
  if (row.type === 'start') {
    return (
      <span className="relative mt-3.5 flex h-8 w-8 items-center justify-center rounded-full text-surface" style={{ background: color }}>
        <Flag className="h-4 w-4" strokeWidth={2.4} />
      </span>
    );
  }
  if (row.type === 'event') {
    return <span className="relative mt-5 h-3.5 w-3.5 rounded-full border-[3px] border-surface" style={{ background: color }} />;
  }
  return <span className="relative mt-[18px] h-3 w-3 rounded-full border-2 bg-surface" style={{ borderColor: color }} />;
}

function RowId({ id, href }) {
  if (!id) return null;
  if (!href) return <span className="font-mono text-xs font-medium text-ink-dim">#{id}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title="Open in the back office"
      className="flex items-center gap-1 font-mono text-xs font-medium text-brand-blue hover:underline">
      #{id} <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// DepositAmount: a deposit as the check measures it — in its own currency (single native fiat)
// or USD — or "not counted" when the CC exception leaves its currency out.
function DepositAmount({ basis, className }) {
  if (!basis) return null;
  return (
    <div className={cn('font-mono', className)}>
      {basis.counted ? (
        <div className="whitespace-nowrap text-sm font-bold text-ink">{formatAmount(basis.amount, basis.currency)}</div>
      ) : (
        <div className="whitespace-nowrap text-xs font-medium text-ink-mute" title="NO more VIP Comp CC exceptions: only the top currency counts">
          not counted
        </div>
      )}
    </div>
  );
}

function Amount({ row, className }) {
  if (row.kind === 'deposit') return <DepositAmount basis={row.basis} className={className} />;
  if (!row.amount) return null;
  return (
    <div className={cn('font-mono', className)}>
      <div className="whitespace-nowrap text-sm font-bold text-ink">{formatAmount(row.amount, row.currency)}</div>
      {row.currency !== 'USD' && row.amountUsd && (
        <div className="whitespace-nowrap text-xs font-medium text-ink-mute">${formatAmount(row.amountUsd)}</div>
      )}
    </div>
  );
}

// StoryRow: one step of the period — time, marker on the line, what happened, amount (its own
// column from sm up; under the text on phones so it never overlaps the labels).
function StoryRow({ row, first, last, afterNow }) {
  return (
    <div className="grid grid-cols-[72px_32px_minmax(0,1fr)] gap-x-3 px-3 sm:grid-cols-[120px_40px_minmax(0,1fr)_auto] sm:px-5">
      <div className="flex flex-col gap-0.5 py-4 text-right font-mono text-xs sm:text-sm">
        <span className="font-medium text-ink">{row.date}</span>
        <span className="font-medium text-ink-mute">{row.time}</span>
      </div>
      <div className="relative flex justify-center">
        <span
          className={cn('absolute left-1/2 w-0.5 -translate-x-1/2', !afterNow && 'bg-brand-blue')}
          style={{ top: first ? 30 : 0, bottom: last ? 'calc(100% - 24px)' : 0, ...(afterNow ? DASHED_LINE : {}) }}
        />
        <Marker row={row} />
      </div>
      <div className="flex min-w-0 flex-col gap-1 py-3.5">
        <div className="flex flex-wrap items-center gap-2">
          {row.type === 'start' && (
            <span className="rounded px-1.5 py-0.5 text-[11px] font-black tracking-wider text-surface" style={{ background: colorOf(row) }}>START</span>
          )}
          <span className="text-sm font-bold" style={{ color: colorOf(row) }}>{row.label}</span>
          {row.badges.map((b) => <Badge key={b.label} variant={b.variant} className="whitespace-nowrap">{b.label}</Badge>)}
          {row.rel && <span className="font-mono text-xs font-medium text-ink-mute">{row.rel}</span>}
        </div>
        {(row.title || row.id || (row.kind === 'deposit' && row.amount)) && (
          <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-ink-dim">
            {row.title && <span>{row.title}</span>}
            {row.kind === 'deposit' && row.amount && (
              <span className="font-mono font-bold text-ink">{formatAmount(row.amount, row.currency)}</span>
            )}
            <RowId id={row.id} href={row.href} />
          </div>
        )}
        {row.times && <div className="font-mono text-xs font-medium text-ink-mute">{row.times}</div>}
        <Amount row={row} className="sm:hidden" />
      </div>
      <Amount row={row} className="hidden py-4 text-right sm:block" />
    </div>
  );
}

function Story({ rows }) {
  const nowIdx = rows.findIndex((r) => r.type === 'now');
  const dashedFrom = nowIdx === -1 ? rows.length - 1 : nowIdx;
  return (
    <div className="min-w-0 flex-[999_1_560px] rounded-xl border border-white/10 bg-surface py-2">
      {rows.map((r, i) => (
        <StoryRow key={r.key} row={r} first={i === 0} last={i === rows.length - 1} afterNow={i >= dashedFrom} />
      ))}
    </div>
  );
}

function Panel({ title, children, style }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-surface-2 p-4" style={style}>
      <span className="text-xs font-bold uppercase tracking-wider text-ink-mute">{title}</span>
      {children}
    </div>
  );
}

// Aside: why the period starts where it does, what is inside it, the legend and data freshness.
function Aside({ period, week, deposits }) {
  const startColor = KIND_COLORS[period.action] || KIND_COLORS.friday;
  return (
    <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
      <Panel title="Why it starts here" style={{ background: `${startColor}1A`, borderColor: `${startColor}40` }}>
        <span className="text-sm font-medium leading-relaxed text-ink">{startReason(period)}</span>
      </Panel>
      <Panel title="Inside the period">
        {periodTotals(period, week, deposits).map((t) => (
          <div key={t.kind} className="flex items-center gap-2.5 text-sm">
            <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLORS[t.kind] }} />
            <span className="flex-1 font-medium text-ink">{t.text}</span>
            <span className="font-mono font-medium text-ink">{t.sum ? formatAmount(t.sum, t.currency) : '—'}</span>
          </div>
        ))}
      </Panel>
      <div className="flex flex-wrap gap-3.5 px-1 text-xs font-medium text-ink-mute">
        {[['friday', 'Friday'], ['bonus', 'Bonus'], ['cashout', 'Cashout'], ['deposit', 'Deposit']].map(([kind, label]) => (
          <span key={kind} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLORS[kind] }} />{label}
          </span>
        ))}
      </div>
      {period.data_as_of && <span className="px-1 text-xs font-medium text-ink-mute">Data as of {formatDateTimeUTC(period.data_as_of)}</span>}
    </aside>
  );
}

// PeriodStory tells the active period: a rail over the week, then from the reset that started it
// through every transaction to now and the period's end, with why it starts there.
export default function PeriodStory({ period, week, now, deposits }) {
  return (
    <div className="space-y-5">
      <Rail rail={railView(period, week, now)} />
      <div className="flex flex-wrap items-start gap-5">
        <Story rows={storyRows(period, week, now, deposits)} />
        <Aside period={period} week={week} deposits={deposits} />
      </div>
    </div>
  );
}
